import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import {
  BbActivityMeta,
  BbSeason,
  bbCreateSeason,
  bbGetActivityMeta,
  bbGetSeasons,
  bbSetActivityMeta,
  bbUpdateSeason,
} from '../../../services/blindBoxService';

// POST /blind-box/seasons requires bb-activity-basic:create OR bb-holiday-lottery:create
const SEASON_CREATE_PERMS = [
  { resource: 'bb-activity-basic', action: 'create' },
  { resource: 'bb-holiday-lottery', action: 'create' },
];
// PUT /blind-box/seasons requires bb-activity-basic:update OR bb-holiday-lottery:update
const SEASON_UPDATE_PERMS = [
  { resource: 'bb-activity-basic', action: 'update' },
  { resource: 'bb-holiday-lottery', action: 'update' },
];

function useActivityMeta(refreshKey: number) {
  const t = useTranslate();
  const notify = useNotify();
  const [meta, setMeta] = useState<BbActivityMeta | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    bbGetActivityMeta()
      .then(setMeta)
      .catch(() => setMeta(null));
  }, [refreshKey]);
  const save = async () => {
    if (!meta) return;
    setSaving(true);
    try {
      await bbSetActivityMeta(meta);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };
  return { meta, setMeta, saving, save };
}

function pickPrimarySeason(seasons: BbSeason[]): BbSeason | null {
  const byStatus = (s: string) => seasons.find((c) => String(c.status).toLowerCase() === s);
  return byStatus('active') || byStatus('upcoming') || null;
}

function toDayjsOrNull(value: unknown): Dayjs | null {
  if (value === null || value === undefined || value === '') return null;
  const d = dayjs(value as any);
  return d.isValid() ? d : null;
}

export function SectionActivityHeroMeta({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const { meta, setMeta, saving, save } = useActivityMeta(refreshKey);
  const [seasons, setSeasons] = useState<BbSeason[]>([]);
  const [seasonsTotal, setSeasonsTotal] = useState(0);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [seasonEdit, setSeasonEdit] = useState<BbSeason | null>(null);
  const [seasonSaving, setSeasonSaving] = useState(false);

  const [seasonCreating, setSeasonCreating] = useState(false);
  const [createDlg, setCreateDlg] = useState(false);
  const [cform, setCform] = useState({ name: '', description: '', startTime: '', endTime: '' });

  const toIsoOrEmpty = (value: Dayjs | null) => (value ? value.toISOString() : '');

  const loadSeasons = useCallback(async () => {
    setSeasonsLoading(true);
    try {
      const offset = page * rowsPerPage;
      const r = await bbGetSeasons(rowsPerPage, offset);
      setSeasons(r.seasons || []);
      setSeasonsTotal(Number(r.total || 0));
    } catch {
      setSeasons([]);
      setSeasonsTotal(0);
    } finally {
      setSeasonsLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons, refreshKey]);

  const primarySeason = useMemo(() => pickPrimarySeason(seasons), [seasons]);

  const createSeason = async () => {
    setSeasonCreating(true);
    try {
      await bbCreateSeason(cform);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setCform({ name: '', description: '', startTime: '', endTime: '' });
      setCreateDlg(false);
      setPage(0);
      await loadSeasons();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSeasonCreating(false);
    }
  };

  const saveSeasonEdit = async () => {
    if (!seasonEdit) return;
    setSeasonSaving(true);
    try {
      await bbUpdateSeason({
        id: seasonEdit.id,
        // seasonNumber: seasonEdit.seasonNumber,
        name: seasonEdit.name,
        // description: seasonEdit.description,
        startTime: seasonEdit.startTime,
        endTime: seasonEdit.endTime,
        // status: seasonEdit.status,
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setSeasonEdit(null);
      await loadSeasons();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSeasonSaving(false);
    }
  };

  if (!meta) return null;

  return (
    <Stack spacing={2}>
      <Box sx={{ pt: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle1">{t('module.blindBox.cyclesList')}</Typography>
            {primarySeason ? (
              <Typography variant="body2" color="text.secondary">
                Primary: {primarySeason.name} ({primarySeason.status})
              </Typography>
            ) : null}
          </Box>
          <PermissionGuard anyOf={SEASON_CREATE_PERMS} fallback={null} loadingFallback={null}>
            <Button startIcon={<AddIcon />} onClick={() => setCreateDlg(true)} disabled={seasonCreating}>
              {t('module.blindBox.createSeason')}
            </Button>
          </PermissionGuard>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>seasonNumber</TableCell>
                <TableCell>{t('module.blindBox.colName')}</TableCell>
                <TableCell>status</TableCell>
                <TableCell>startTime</TableCell>
                <TableCell>endTime</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {seasons.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{String(s.seasonNumber ?? '')}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{String(s.status ?? '')}</TableCell>
                  <TableCell>{toDayjsOrNull(s.startTime)?.format('YYYY-MM-DD HH:mm') || '—'}</TableCell>
                  <TableCell>{toDayjsOrNull(s.endTime)?.format('YYYY-MM-DD HH:mm') || '—'}</TableCell>
                  <TableCell align="right">
                    <PermissionGuard anyOf={SEASON_UPDATE_PERMS} fallback={null} loadingFallback={null}>
                      <IconButton size="small" onClick={() => setSeasonEdit({ ...s })}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </PermissionGuard>
                  </TableCell>
                </TableRow>
              ))}
              {!seasonsLoading && !seasons.length ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('module.blindBox.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={seasonsTotal}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10) || 10);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>

      <Dialog open={createDlg} onClose={() => setCreateDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('module.blindBox.createSeason')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('module.blindBox.seasonName')}
              value={cform.name}
              onChange={(e) => setCform((p) => ({ ...p, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t('module.blindBox.seasonDesc')}
              value={cform.description}
              onChange={(e) => setCform((p) => ({ ...p, description: e.target.value }))}
              fullWidth
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="startTime"
                value={cform.startTime ? dayjs(cform.startTime) : null}
                onChange={(v) => setCform((p) => ({ ...p, startTime: toIsoOrEmpty(v) }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="endTime"
                value={cform.endTime ? dayjs(cform.endTime) : null}
                onChange={(v) => setCform((p) => ({ ...p, endTime: toIsoOrEmpty(v) }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={seasonCreating} onClick={() => setCreateDlg(false)}>
            {t('actions.cancel')}
          </Button>
          <PermissionGuard anyOf={SEASON_CREATE_PERMS} fallback={null} loadingFallback={null}>
            <Button variant="contained" disabled={seasonCreating} onClick={createSeason}>
              {t('actions.save')}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>

      <Dialog open={!!seasonEdit} onClose={() => setSeasonEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('module.blindBox.editSeason')}</DialogTitle>
        <DialogContent>
          {seasonEdit ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="id" value={seasonEdit.id} disabled fullWidth />

              <TextField
                label={t('module.blindBox.colName')}
                value={seasonEdit.name || ''}
                onChange={(e) => setSeasonEdit({ ...seasonEdit, name: e.target.value })}
                fullWidth
              />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="startTime"
                  value={toDayjsOrNull(seasonEdit.startTime)}
                  onChange={(v) => setSeasonEdit({ ...seasonEdit, startTime: v ? v.toISOString() : '' } as any)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="endTime"
                  value={toDayjsOrNull(seasonEdit.endTime)}
                  onChange={(v) => setSeasonEdit({ ...seasonEdit, endTime: v ? v.toISOString() : '' } as any)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={seasonSaving} onClick={() => setSeasonEdit(null)}>
            {t('actions.cancel')}
          </Button>
          <PermissionGuard anyOf={SEASON_UPDATE_PERMS} fallback={null} loadingFallback={null}>
            <Button variant="contained" disabled={seasonSaving} onClick={saveSeasonEdit}>
              {t('actions.save')}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export function SectionActivityTopBarMeta({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const { meta, setMeta, saving, save } = useActivityMeta(refreshKey);
  if (!meta) return null;
  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle1">{t('module.blindBox.h5TopBar')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('module.blindBox.h5TopBarCopy')}
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label={t('module.blindBox.navbarTitle')}
            value={meta.navbarTitle}
            onChange={(e) => setMeta({ ...meta, navbarTitle: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={t('module.blindBox.backButtonText')}
            value={meta.backButtonText}
            onChange={(e) => setMeta({ ...meta, backButtonText: e.target.value })}
            fullWidth
          />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="flex-end">
        <PermissionButton resource="bb-activity-basic" action="update">
          <Button variant="contained" onClick={save} disabled={saving}>
            {t('actions.save')}
          </Button>
        </PermissionButton>
      </Stack>
    </Stack>
  );
}
